import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Progress } from "@/components/ui/progress"
import { Target, TrendingUp, Calendar, Award, Plus } from "lucide-react"

export default function Metas() {
  const metas = [
    {
      id: 1,
      titulo: "Vendas Mensais",
      descricao: "Meta de vendas para janeiro de 2024",
      tipo: "Vendas",
      valor: 3200000,
      atual: 2800000,
      unidade: "R$",
      periodo: "Janeiro 2024",
      progresso: 87.5,
      status: "Em Andamento"
    },
    {
      id: 2,
      titulo: "Instalações kW",
      descricao: "Potência total instalada no mês",
      tipo: "Técnico",
      valor: 2000,
      atual: 1847,
      unidade: "kW",
      periodo: "Janeiro 2024",
      progresso: 92.4,
      status: "Quase Atingida"
    },
    {
      id: 3,
      titulo: "Novos Clientes",
      descricao: "Aquisição de novos clientes no mês",
      tipo: "Aquisição",
      valor: 30,
      atual: 24,
      unidade: "clientes",
      periodo: "Janeiro 2024",
      progresso: 80,
      status: "Em Andamento"
    },
    {
      id: 4,
      titulo: "Taxa de Conversão",
      descricao: "Conversão de leads para clientes",
      tipo: "Performance",
      valor: 25,
      atual: 22.5,
      unidade: "%",
      periodo: "Janeiro 2024",
      progresso: 90,
      status: "Atingida"
    }
  ]

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Atingida": return "default"
      case "Quase Atingida": return "secondary"
      case "Em Andamento": return "outline"
      default: return "secondary"
    }
  }

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case "Vendas": return "default"
      case "Técnico": return "outline"
      case "Aquisição": return "secondary"
      case "Performance": return "destructive"
      default: return "secondary"
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Metas</h1>
          <p className="text-muted-foreground">Objetivos e KPIs do negócio</p>
        </div>
        <Button className="gap-2">
          <Plus className="h-4 w-4" />
          Nova Meta
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Metas Ativas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metas.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Atingidas</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">
              {metas.filter(m => m.status === "Atingida").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Em Andamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">
              {metas.filter(m => m.status === "Em Andamento").length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Performance Média</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {Math.round(metas.reduce((acc, m) => acc + m.progresso, 0) / metas.length)}%
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Acompanhamento de Metas
          </CardTitle>
          <CardDescription>
            Progresso detalhado de cada objetivo
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {metas.map((meta) => (
              <div key={meta.id} className="p-4 border border-border rounded-lg space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{meta.titulo}</h3>
                    <p className="text-sm text-muted-foreground">{meta.descricao}</p>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant={getTipoColor(meta.tipo)}>
                      {meta.tipo}
                    </Badge>
                    <Badge variant={getStatusColor(meta.status)}>
                      {meta.status}
                    </Badge>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>{meta.periodo}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-muted-foreground" />
                    <span>
                      {meta.unidade === "R$" ? "R$ " : ""}
                      {meta.atual.toLocaleString('pt-BR')}
                      {meta.unidade !== "R$" ? ` ${meta.unidade}` : ""}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-4 w-4 text-muted-foreground" />
                    <span>
                      Meta: {meta.unidade === "R$" ? "R$ " : ""}
                      {meta.valor.toLocaleString('pt-BR')}
                      {meta.unidade !== "R$" ? ` ${meta.unidade}` : ""}
                    </span>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progresso</span>
                    <span>{meta.progresso.toFixed(1)}%</span>
                  </div>
                  <Progress value={meta.progresso} className="h-2" />
                </div>

                <div className="flex gap-2">
                  <Button variant="outline" size="sm">
                    Ver Detalhes
                  </Button>
                  <Button variant="outline" size="sm">
                    Editar Meta
                  </Button>
                  <Button variant="outline" size="sm">
                    Histórico
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
